import { capitalizeFirstLetter } from "app/main/utils/tableUtils";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import {
	Assignment as AssignmentIcon,
	LocalShipping as ExecutionIcon,
	EventNote as BookingIcon,
} from "@material-ui/icons";
import moment from "moment";

const getCategoryIcon = (category) => {
	switch (category) {
		case "Execution":
			return <ExecutionIcon style={{ fontSize: "1rem", marginRight: 4, color: "gray" }} />;
		case "Booking":
			return <BookingIcon style={{ fontSize: "1rem", marginRight: 4, color: "gray" }} />;
		case "Assignment":
			return <AssignmentIcon style={{ fontSize: "1rem", marginRight: 4, color: "gray" }} />;
		default:
			return null;
	}
};

const getTimeElapsed = (createdAt, updatedAt) => {
	const now = moment();
	const created = moment(createdAt);
	const updated = moment(updatedAt);

	// Use the more recent date (updated or created)
	const referenceDate = updated.isAfter(created) ? updated : created;

	const duration = moment.duration(now.diff(referenceDate));

	const days = Math.floor(duration.asDays());
	const hours = duration.hours();
	const minutes = duration.minutes();

	if (days > 0) {
		return `${days}d ${hours}h`;
	} else if (hours > 0) {
		return `${hours}h ${minutes}m`;
	} else {
		return `${minutes}m`;
	}
};

const getPriorityColor = (priority) => {
	switch (priority) {
		case "Critical":
			return { color: "#c62828", fontWeight: 700 }; // Red, bold
		case "High":
			return { color: "#ef6c00", fontWeight: 600 }; // Orange, semibold
		case "Medium":
			return { color: "#f9a825", fontWeight: 500 }; // Yellow/amber
		case "Low":
			return { color: "#1565c0", fontWeight: 400 }; // Blue
		default:
			return { color: "#757575", fontWeight: 400 }; // Gray
	}
};

const getStatusChipStyle = (status) => {
	switch (status) {
		case "Open":
			return { backgroundColor: "#ffebee", color: "#c62828" }; // Light red bg, dark red text
		case "In Progress":
			return { backgroundColor: "#e3f2fd", color: "#1565c0" }; // Light blue bg, dark blue text
		case "Resolved":
			return { backgroundColor: "#e8f5e8", color: "#2e7d32" }; // Light green bg, dark green text
		case "Closed":
			return { backgroundColor: "#f5f5f5", color: "#616161" }; // Light gray bg, dark gray text
		default:
			return { backgroundColor: "#f5f5f5", color: "#757575" }; // Default gray
	}
};

const opsExceptions = {
	urlGET: "api/profile/carriers/:carrierId/ops-exceptions",
	idKey: "_id",
	listenEvent: ["opsExceptionRevision", "profileRevision"],
	segmentKey: "OPS_EXCEPTIONS",
	content: {
		table: {
			variant: "skinny",
			layout: ["status", "time_elapsed", "priority", "title", "description", "trip", "truck", "updatedAt"],
			orderBy: ["status", "priority", "title", "updatedAt"],
		},
		pagination: {
			enabled: true,
			resetScroll: true,
		},
		order: {
			default: { key: "priority", dir: "desc" },
			desc: ["priority", "updatedAt"],
			asc: ["title", "category", "status"],
			options: [
				{ key: "priority", label: "Priority" },
				{ key: "status", label: "Status" },
				{ key: "category", label: "Category" },
				{ key: "title", label: "Title" },
				{ key: "updatedAt", label: "Updated Date" },
			],
		},
		defaults: {
			initial: {
				filters: {
					exception_active: "ACTIVE",
					status: "Open",
				},
			},
			items: [
				{
					label: "Open",
					description: "Open Exceptions",
					filters: {
						exception_active: "ACTIVE",
						status: "Open",
					},
				},
				{
					label: "In Progress",
					description: "In Progress",
					filters: {
						exception_active: "ACTIVE",
						status: "In Progress",
					},
				},
				{
					label: "All Active",
					description: "All Active",
					filters: {
						exception_active: "ACTIVE",
					},
				},
				{
					label: "Resolved",
					description: "Resolved",
					filters: {
						exception_active: "ACTIVE",
						status: "Resolved",
					},
				},
			],
		},
		filters: {
			search: true,
			countVisible: 3,
			items: [
				{
					key: "exception_active",
					type: "select",
					label: "Active Status",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "Not Selected",
						},
						{
							label: "Active",
							value: "ACTIVE",
						},
						{
							label: "Archived",
							value: "INACTIVE",
						},
					],
				},
				{
					key: "status",
					type: "select",
					label: "Status",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							label: "Open",
							value: "Open",
						},
						{
							label: "In Progress",
							value: "In Progress",
						},
						{
							label: "Resolved",
							value: "Resolved",
						},
						{
							label: "Closed",
							value: "Closed",
						},
					],
				},
				{
					key: "category",
					type: "select",
					label: "Category",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							label: "Execution",
							value: "Execution",
						},
						{
							label: "Booking",
							value: "Booking",
						},
						{
							label: "Assignment",
							value: "Assignment",
						},
					],
				},
				{
					key: "priority",
					type: "select",
					label: "Priority",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							label: "Critical",
							value: "Critical",
						},
						{
							label: "High",
							value: "High",
						},
						{
							label: "Medium",
							value: "Medium",
						},
						{
							label: "Low",
							value: "Low",
						},
					],
				},
			],
		},
		items: [
			{
				key: "status",
				type: "component",
				label: "Status",
				table: { width: "10%" },
				builder: (item) => {
					const style = getStatusChipStyle(item.status);
					const icon = getCategoryIcon(item.category);
					return (
						<Chip
							size="small"
							icon={icon}
							label={item.status}
							style={{
								...style,
								fontSize: "0.75rem",
								height: "26px",
								padding: "0 8px",
							}}
						/>
					);
				},
				export: {
					builder: (item) => `${item.category} - ${item.status}`,
				},
			},
			{
				key: "time_elapsed",
				type: "component",
				label: "Time",
				table: { width: "6%" },
				builder: (item) => {
					const elapsed = getTimeElapsed(item.createdAt, item.updatedAt);
					return (
						<Typography variant="body2" style={{ fontSize: "0.8rem", color: "#757575" }}>
							{elapsed}
						</Typography>
					);
				},
				export: {
					builder: (item) => getTimeElapsed(item.createdAt, item.updatedAt),
				},
			},
			{
				key: "priority",
				type: "component",
				label: "Priority",
				table: { width: "8%" },
				builder: (item) => {
					const style = getPriorityColor(item.priority);
					return (
						<Typography variant="body2" style={{ ...style, fontSize: "0.85rem" }}>
							{item.priority}
						</Typography>
					);
				},
				export: {
					builder: (item) => item.priority,
				},
			},
			{
				key: "title",
				type: "text",
				label: "Title",
				table: { width: "12%" },
			},
			{
				key: "description",
				type: "text",
				label: "Description",
				table: { width: "32%" },
				builder: (item) => {
					return item.description || "";
				},
			},
			{
				key: "trip",
				type: "trips__view",
				label: "Trip",
				table: { width: "10%" },
				builder: (item) => {
					if (!item.trip_info) return null;
					return {
						value: item.trip_info._id,
						label: item.trip_info.data?.load_id || item.trip_info.data?.trip_id || "N/A",
						metadata: {
							status: item.trip_info.data?.status,
							carrier: item.trip_info.carrier,
							equipment: item.trip_info.data?.equipment,
							broker: item.trip_info.data?.broker,
						},
					};
				},
				viewConfig: {
					showStatus: true,
					showBroker: true,
				},
			},
			{
				key: "truck",
				type: "truck__view",
				label: "Truck",
				table: { width: "10%" },
				builder: (item) => {
					if (!item.truck_info) return null;
					return {
						value: item.truck_info._id,
						label: item.truck_info.truckid || "N/A",
						metadata: {
							carrier: item.truck_info.carrier,
							active: item.truck_info.active,
							trailerid: item.truck_info.trailer,
							driver__view: {
								value: item.truck_info.driver,
								label: item.truck_info.driver__view?.name || "N/A",
								metadata: {
									phone: item.truck_info.driver__view?.phone || "N/A",
								},
							},
						},
					};
				},
				viewConfig: {
					showPhone: true,
					showDriver: true,
				},
			},
			{
				key: "updatedAt",
				type: "date",
				label: "Updated",
				table: { width: "8%" },
			},
			{
				key: "menu",
				type: "menu",
				label: "Menu",
				items: [
					{
						key: "edit",
						label: "Edit",
						auth: ["carrier", "carrier_manager", "carrier_dispatcher", "administrator"],
						dialogBuilder: (item, dataIds) => ({
							viewId: "OPS_EXCEPTION_VIEW",
							mode: "EDIT",
							dataIds: { ...dataIds, id: item._id, exceptionId: item._id, mode: "EDIT" },
						}),
					},
					{
						key: "divider",
						variant: "divider",
						auth: ["carrier", "carrier_manager", "carrier_dispatcher", "administrator"],
					},
					{
						key: "mark_in_progress",
						label: "Mark In Progress",
						auth: ["carrier", "carrier_manager", "carrier_dispatcher", "administrator"],
						dialogBuilder: (item, dataIds) => ({
							formId: "OPS_EXCEPTION_STATUS_FORM",
							mode: "DELETE",
							dataIds: { ...dataIds, id: item._id, status: "In Progress" },
						}),
						isVisible: (item) => item.status === "Open",
					},
					{
						key: "mark_resolved",
						label: "Mark Resolved",
						auth: ["carrier", "carrier_manager", "carrier_dispatcher", "administrator"],
						dialogBuilder: (item, dataIds) => ({
							formId: "OPS_EXCEPTION_STATUS_FORM",
							mode: "DELETE",
							dataIds: { ...dataIds, id: item._id, status: "Resolved" },
						}),
						isVisible: (item) => item.status === "Open" || item.status === "In Progress",
					},
					{
						key: "mark_closed",
						label: "Mark Closed",
						auth: ["carrier", "carrier_manager", "carrier_dispatcher", "administrator"],
						dialogBuilder: (item, dataIds) => ({
							formId: "OPS_EXCEPTION_STATUS_FORM",
							mode: "DELETE",
							dataIds: { ...dataIds, id: item._id, status: "Closed" },
						}),
						isVisible: (item) => item.status === "Resolved",
					},
					{
						key: "divider2",
						variant: "divider",
						auth: ["carrier", "carrier_manager", "carrier_dispatcher", "administrator"],
					},
					{
						key: "activate",
						label: "Activate",
						auth: ["carrier", "carrier_manager", "carrier_dispatcher", "administrator"],
						dialogBuilder: (item, dataIds) => ({
							formId: "OPS_EXCEPTION_STATUS_FORM",
							mode: "DELETE",
							dataIds: { ...dataIds, id: item._id, status: "active" },
						}),
						isVisible: (item) => !item.active,
					},
					{
						key: "archive",
						label: "Archive",
						auth: ["carrier", "carrier_manager", "carrier_dispatcher", "administrator"],
						dialogBuilder: (item, dataIds) => ({
							formId: "OPS_EXCEPTION_STATUS_FORM",
							mode: "DELETE",
							dataIds: { ...dataIds, id: item._id, status: "inactive", item: item },
						}),
						isVisible: (item) => item.active,
					},
				],
			},
		],
	},
};

export default opsExceptions;
